import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
} from 'react-hook-form'
import { View, ViewStyle } from 'react-native'
import { TextInputProps } from 'react-native-paper'
import { StyleSheet } from 'react-native-unistyles'

import { getErrorField } from '@/components/form/formUtils'
import TextInput from '@/components/form/TextInput'
import Skeleton from '@/components/shared/Skeleton'
import UniHelperText from '@/components/unistyles/UniHelperText'

interface Props<C extends FieldValues, E extends FieldErrors> {
  fieldName: Path<C>
  required: boolean
  errors?: E
  control: Control<C, any>
  isLoading?: boolean
  onBlur?: () => void
}

const FormFieldNumber = <C extends FieldValues, E extends FieldErrors>({
  fieldName,
  required,
  errors,
  control,
  isLoading,
  style,
  onBlur,
  ...props
}: Props<C, E> & TextInputProps) => {
  return (
    <View style={[style as ViewStyle, styles.root]}>
      <Skeleton isLoading={isLoading}>
        <Controller
          control={control}
          name={fieldName}
          render={({ field: { onChange, onBlur: formOnBlur, value } }) => (
            <View style={styles.container}>
              <TextInput
                accessibilityHint="This is a number field"
                accessibilityLabel="Number input field"
                testID={`${fieldName}- numberInput`}
                {...props}
                contentStyle={styles.container}
                error={!!getErrorField(fieldName, errors)}
                keyboardType="numeric" // This is the key prop for numeric input
                label={props.label as string}
                onBlur={() => {
                  onBlur?.()
                  formOnBlur()
                }}
                onChangeText={(text) => {
                  // Ensure only numbers are accepted
                  const numericValue = text.replace(/[^0-9]/g, '') // Remove non-numeric characters
                  onChange(+numericValue)
                }}
                value={value?.toString() || ''}
              />
              {getErrorField(fieldName, errors) && (
                <UniHelperText
                  type="error"
                  visible={!!getErrorField(fieldName, errors)}
                >
                  {getErrorField(fieldName, errors)?.message as string}
                </UniHelperText>
              )}
            </View>
          )}
          rules={{
            required: required,
          }}
        />
      </Skeleton>
    </View>
  )
}

const styles = StyleSheet.create((theme) => ({
  container: {
    flexWrap: 'nowrap',
  },
  root: {
    marginBottom: theme.tokens.spacing[2],
  },
}))

export default FormFieldNumber
